import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import FAQ from '@/components/base/FAQ';
import { categories, articles } from '@/mocks/articles';

export default function Home() {
  const { t } = useTranslation();

  const featuredArticles = articles.slice(0, 6);

  const faqItems = [
    {
      question: 'USB接口可以拔插多少次？',
      answer: '标准USB-A接口的设计拔插寿命约为1,500至5,000次，USB-C接口约为10,000次。实际寿命取决于使用频率、操作方式和环境条件。以每天3次计算，USB-C接口理论上可以正常使用约9年。正确的拔插手法和定期清洁维护可以显著延长接口的实际使用寿命。',
    },
    {
      question: '拔插时需要注意哪些安全事项？',
      answer: '拔插操作的安全核心原则有三条：第一，涉及电源的接口必须先断电再拔插；第二，始终握住插头本体而非拉扯线缆进行拔插；第三，保持插头与接口平面垂直，匀速操作。此外，湿手不操作电器、雷雨天气拔掉重要设备电源、不确定接口是否支持热拔插时先断电，这些都是必须牢记的安全准则。',
    },
    {
      question: 'Type-C和USB-A哪个更耐用？',
      answer: 'Type-C接口的耐用性明显优于USB-A。Type-C设计标准要求至少10,000次拔插寿命，而传统USB-A通常为1,500-5,000次。Type-C还具备对称设计（正反可插，减少误操作）、更坚固的锁紧机构和更先进的触片材料等优势。在实际使用测试中，同等条件下Type-C的磨损速度仅为USB-A的30%-40%。',
    },
    {
      question: '接口松动了还能修复吗？',
      answer: '轻微松动可能是正常磨损或灰尘积累导致，可以先尝试用压缩空气清洁接口内部。如果松动严重影响使用，需要判断是线缆端还是设备端的问题：线缆端直接更换线缆即可；设备端接口松动建议送专业维修，不建议自行拆卸。日常使用中避免拉扯线缆拔插可以有效延缓接口松动。',
    },
    {
      question: '如何正确清洁设备接口？',
      answer: '接口清洁的正确步骤：1) 关闭设备电源；2) 使用压缩空气罐（保持罐体直立）吹除灰尘；3) 用干燥的防静电刷轻轻刷拭触片；4) 顽固污渍可用99%异丙醇和无尘布轻轻擦拭；5) 等待完全干燥后再使用。严禁使用水、含油清洁剂或金属工具清洁接口。建议每3-6个月清洁一次。',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: t('siteName'),
    url: window.location.origin,
    description: t('siteDescription'),
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${window.location.origin}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const webPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('home.heroTitle') + ' - ' + t('siteName'),
    description: t('siteDescription'),
    url: window.location.origin,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageJsonLd)}</script>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20electronic%20device%20cable%20management%20scene%20with%20various%20USB%20and%20Type-C%20connectors%20arranged%20in%20clean%20geometric%20pattern%20on%20light%20cream%20surface%2C%20soft%20natural%20lighting%20from%20left%2C%20architectural%20composition%2C%20warm%20neutral%20cream%20and%20beige%20tones%2C%20artistic%20abstract%20style%2C%20professional%20product%20photography&width=1800&height=1000&seq=hero-bg-home-13&orientation=landscape"
                alt="拔插拔插操作指南"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
            </div>
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <div className="max-w-4xl mx-auto">
                <p className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm text-background-50 text-sm font-medium mb-6 border border-background-50/20">
                  <i className="ri-sparkling-line"></i>
                  全网最全面的拔插操作指南
                </p>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-background-50 mb-6 font-heading tracking-tight">
                  {t('home.heroTitle')}
                </h1>
                <p className="text-xl md:text-2xl text-background-50/90 font-heading font-medium mb-4">
                  {t('home.heroSubtitle')}
                </p>
                <p className="text-base md:text-lg text-background-50/70 max-w-2xl mx-auto mb-10">
                  {t('home.heroDesc')}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/category/caozuo-fangfa"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-semibold text-base hover:bg-primary-600 transition-all duration-300 hover:gap-3 whitespace-nowrap"
                  >
                    {t('home.startReading')}
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="#categories"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full font-semibold text-base hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap border border-background-50/20"
                  >
                    {t('home.viewCategories')}
                    <i className="ri-arrow-down-line"></i>
                  </Link>
                </div>
              </div>
            </div>
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
              <i className="ri-arrow-down-line text-background-50/60 text-2xl"></i>
            </div>
          </section>

          {/* Stats Bar */}
          <section className="bg-background-50 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '28', label: '篇专业文章', icon: 'ri-article-line' },
                  { value: '6', label: '大专题分类', icon: 'ri-stack-line' },
                  { value: '50,000+', label: '每月读者', icon: 'ri-user-line' },
                  { value: '99.8%', label: '读者好评率', icon: 'ri-heart-line' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className={`${stat.icon} text-lg`}></i>
                    </div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{stat.value}</div>
                    <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section id="categories" className="py-16 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4 font-heading">
                  {t('home.categoriesTitle')}
                </h2>
                <p className="text-foreground-500 max-w-xl mx-auto">
                  {t('home.categoriesSubtitle')}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group bg-background-50 rounded-lg p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950 mb-2 font-heading group-hover:text-primary-500 transition-colors duration-200">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                      {cat.description}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-400">{cat.articleCount} 篇文章</span>
                      <span className="text-primary-500 flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                        浏览分类
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="py-16 md:py-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3 font-heading">
                    {t('home.featuredArticles')}
                  </h2>
                  <p className="text-foreground-500 text-sm">
                    精选高质量拔插知识文章，助你成为拔插达人
                  </p>
                </div>
                <Link
                  to="/category/caozuo-fangfa"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  {t('home.allArticles')}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3 font-heading">
                  {t('home.faqTitle')}
                </h2>
                <p className="text-foreground-500 text-sm">
                  {t('home.faqSubtitle')}
                </p>
              </div>
              <FAQ items={faqItems} />
            </div>
          </section>

          {/* CTA Section */}
          <section className="relative py-20 md:py-28 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20neatly%20organized%20electronics%20cables%20and%20connectors%20on%20minimalist%20desk%2C%20warm%20ambient%20lighting%2C%20cream%20and%20beige%20toned%20interior%2C%20soft%20focus%20background%2C%20professional%20lifestyle%20photography&width=1800&height=800&seq=cta-bg-home-14&orientation=landscape"
                alt=""
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40"></div>
            </div>
            <div className="relative z-10 text-center px-4 md:px-6">
              <h2 className="text-3xl md:text-4xl font-bold text-background-50 mb-4 font-heading">
                {t('home.ctaTitle')}
              </h2>
              <p className="text-background-50/80 text-lg mb-8 max-w-lg mx-auto">
                {t('home.ctaSubtitle')}
              </p>
              <Link
                to="/category/caozuo-fangfa"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full font-semibold text-base hover:bg-primary-600 transition-all duration-300 hover:gap-3 whitespace-nowrap"
              >
                {t('home.ctaButton')}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}